import { NodeEdge } from "@hex-insights/app-modules";
import { ClassNameProps, StyleProps } from "@hex-insights/core";
import {
	BigEventCalendar,
	BigEventCalendarView,
	EventCalendar,
	EventCalendarItem,
	getCalendarEventFiltersForViewerForDateRange,
} from "../../../../Components/EventCalendar";
import {
	CalendarEvent,
	CalendarEventFilterInput,
	mergeFilterInputs,
	SharingPolicy,
	useCalendarEventCalendarListQuery,
	UserGroup,
} from "../../../../Utilities";

export type ViewerCalendarProps = {
	initialDate?: Date;
	withBirthdays?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function ViewerCalendar({ initialDate, withBirthdays = true, className, style }: ViewerCalendarProps) {
	const { value, onChange, onActiveStartDateChange, onClickMonth, firstOfMonth, lastOfMonth } =
		EventCalendar.useDates(initialDate);

	const { loading, data } = useCalendarEventCalendarListQuery({
		variables: {
			filters: getCalendarEventFiltersForViewerForDateRange(firstOfMonth, lastOfMonth, true),
		},
	});

	return (
		<EventCalendar
			value={value}
			onChange={onChange}
			onActiveStartDateChange={onActiveStartDateChange}
			onClickMonth={onClickMonth}
			noSecondaryNavigation
			withBirthdays={withBirthdays}
			events={calendarEventEdgesToEventCalendarList(data?.calendarEventConnection.edges)}
			isLoading={loading}
			className={className}
			style={style}
		/>
	);
}

export type ViewerBigCalendarProps = {
	initialDate?: Date;
	initialView?: BigEventCalendarView;
	detailModalVariant?: "default" | "admin";
	filters?: CalendarEventFilterInput;
} & Partial<ClassNameProps & StyleProps>;

export function ViewerBigCalendar({
	initialDate,
	initialView,
	detailModalVariant = "default",
	filters,
	className,
	style,
}: ViewerBigCalendarProps) {
	const { value, onChange, firstOfMonth, lastOfMonth } = EventCalendar.useDates(initialDate);

	const baseFilters = getCalendarEventFiltersForViewerForDateRange(firstOfMonth, lastOfMonth, true);
	const { data } = useCalendarEventCalendarListQuery({
		variables: {
			filters: filters ? mergeFilterInputs(filters, baseFilters) : baseFilters,
		},
	});

	return (
		<BigEventCalendar
			value={value}
			onChange={onChange}
			events={calendarEventEdgesToEventCalendarList(data?.calendarEventConnection.edges)}
			detailModalVariant={detailModalVariant}
			initialView={initialView}
			className={className}
			style={style}
		/>
	);
}

function calendarEventEdgesToEventCalendarList<
	T extends Pick<
		CalendarEvent,
		"id" | "name" | "description" | "isAllDay" | "startTime" | "endTime" | "startDate" | "endDate"
	>,
	UG extends Pick<UserGroup, "id" | "name" | "color">,
>(edges?: NodeEdge<T & { sharingPolicy: (Pick<SharingPolicy, "id" | "isPublic"> & { userGroups: UG[] }) | null }>[]) {
	if (!edges) {
		return [];
	}

	const list: EventCalendarItem[] = [];
	for (let i = 0; i < edges.length; i++) {
		const node = edges[i].node;
		list.push({
			...node,
			isPublic: node.sharingPolicy?.isPublic ?? false,
			groups: node.sharingPolicy?.userGroups ?? [],
		});
	}
	return list;
}
